import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Bell, GraduationCap, FileText, Mail, Megaphone, ShieldCheck, Download, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import ConsentModal from "@/components/ConsentModal";

const CATEGORIES = [
  { key: "exam_reminders", label: "Sınav Hatırlatmaları", desc: "Yaklaşan deneme sınavları için 24s / 3s / 1s / 10dk önce bildirim.", icon: GraduationCap },
  { key: "application_updates", label: "Başvuru Güncellemeleri", desc: "Bursluluk başvurunuzun durumu değiştiğinde.", icon: FileText },
  { key: "invitations", label: "Okul Davetleri & Burs Teklifleri", desc: "Okullardan gelen davet ve teklifler için.", icon: Mail },
  { key: "subscription_reminders", label: "Abonelik Hatırlatmaları", desc: "Aboneliğin bitmeden 3 gün önce hatırlatma bildirimi.", icon: Bell },
  { key: "marketing", label: "Kampanya ve Duyurular", desc: "Yeni özellikler, indirimler ve platform duyuruları.", icon: Megaphone },
];

export default function NotificationSettings() {
  const [prefs, setPrefs] = useState(null);
  const [consentStatus, setConsentStatus] = useState(null);
  const [consentOpen, setConsentOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.get(`${V1}/users/me/notification-prefs`).then((r) => setPrefs(r.data));
    api.get(`${V1}/users/me/consent-status`).then((r) => setConsentStatus(r.data)).catch(() => {});
  }, []);

  const toggle = async (key, val) => {
    const next = { ...prefs, [key]: val };
    setPrefs(next);
    try {
      await api.put(`${V1}/users/me/notification-prefs`, { [key]: val });
    } catch { toast.error("Kaydedilemedi"); }
  };

  const submitConsent = async (payload) => {
    try {
      await api.post(`${V1}/users/me/consent`, { kvkk: payload.kvkk, terms: payload.terms, marketing: payload.marketing });
      setConsentStatus({ needs_reconsent: false });
      toast.success("Onayınız kaydedildi. Teşekkürler!");
    } catch { toast.error("Onay kaydedilemedi"); }
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const { data } = await api.get(`${V1}/users/me/data-export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `sanostudent-verilerim-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      toast.success("Verileriniz indirildi");
    } catch { toast.error("Veri dışa aktarılamadı"); }
    finally { setExporting(false); }
  };

  const deleteAccount = async () => {
    if (!window.confirm("Hesabınız ve tüm verileriniz (çocuk profilleri, sınav geçmişi, başvurular) KALICI olarak silinecek. Onaylıyor musunuz?")) return;
    setDeleting(true);
    try {
      await api.delete(`${V1}/users/me`);
      toast.success("Hesabınız silindi");
      localStorage.removeItem("sano_token");
      window.location.href = "/";
    } catch (e) { toast.error(e?.response?.data?.detail || "Hesap silinemedi"); setDeleting(false); }
  };

  if (!prefs) return <div className="text-muted-foreground">Yükleniyor…</div>;

  return (
    <div className="space-y-6" data-testid="notification-settings-page">
      <div>
        <div className="overline text-accent">Bildirim Tercihleri</div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Bell className="h-7 w-7" /> Bildirim & Gizlilik Ayarları</h1>
        <p className="text-muted-foreground mt-1">Hangi bildirim kategorilerini almak istediğinizi seçin. Ayarlar hem web hem mobile için geçerlidir.</p>
      </div>

      {consentStatus?.needs_reconsent && (
        <div className="p-4 rounded-xl border border-amber-400/40 bg-amber-500/10 flex items-center justify-between gap-4 flex-wrap" data-testid="reconsent-banner">
          <div className="flex items-center gap-3 text-sm">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
            <span>KVKK Aydınlatma Metni güncellendi. Devam etmek için lütfen yeniden onaylayın.</span>
          </div>
          <Button size="sm" onClick={() => setConsentOpen(true)} data-testid="reconsent-btn">Metni Gör ve Onayla</Button>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card divide-y divide-border">
        {CATEGORIES.map((c) => (
          <div key={c.key} className="flex items-start justify-between p-5 gap-4" data-testid={`pref-row-${c.key}`}>
            <div className="flex gap-4 flex-1">
              <div className="mt-1 flex h-10 w-10 items-center justify-center rounded-md bg-accent/10 text-accent shrink-0">
                <c.icon className="h-5 w-5" />
              </div>
              <div>
                <div className="font-display font-bold">{c.label}</div>
                <div className="text-sm text-muted-foreground mt-1">{c.desc}</div>
              </div>
            </div>
            <Switch
              checked={!!prefs[c.key]}
              onCheckedChange={(v) => toggle(c.key, v)}
              data-testid={`pref-switch-${c.key}`}
            />
          </div>
        ))}
      </div>
      <div className="text-xs text-muted-foreground">
        Yasal olarak zorunlu güvenlik ve işlem bildirimleri (SMS doğrulama, kritik hesap uyarıları) her durumda gönderilir.
      </div>

      {/* KVKK: veri taşınabilirliği + hesap silme */}
      <div className="rounded-xl border border-border bg-card p-5" data-testid="kvkk-privacy-section">
        <div className="font-display font-bold flex items-center gap-2 mb-1">
          <ShieldCheck className="h-5 w-5 text-accent" /> Verilerim (KVKK)
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          KVKK md. 11 kapsamında tüm kişisel verinizi indirebilir veya hesabınızı kalıcı olarak silebilirsiniz.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={exportData} disabled={exporting} data-testid="export-data-btn">
            <Download className="h-4 w-4" /> {exporting ? "Hazırlanıyor…" : "Verilerimi İndir"}
          </Button>
          <Button variant="destructive" onClick={deleteAccount} disabled={deleting} data-testid="delete-account-btn">
            <Trash2 className="h-4 w-4" /> {deleting ? "Siliniyor…" : "Hesabımı Sil"}
          </Button>
        </div>
      </div>

      <ConsentModal open={consentOpen} onOpenChange={setConsentOpen} onAccept={submitConsent} />
    </div>
  );
}
